import React, {useMemo} from 'react';

import {TFunction} from 'i18next';
import {useTranslation} from 'react-i18next';

import {Select, SelectOption} from 'components/ui-kit/Select';
import {InnerSortings, InnerSortingsElement} from 'types';

import {FlatGridFieldConfig} from '../types';

interface SortValue {
    id: string;
    desc?: boolean;
}

const mapSelectOptions = (config: FlatGridFieldConfig[], t: TFunction) =>
    config.reduce((accumulator, currentValue) => {
        const desc = {
            value: {id: currentValue.id, desc: true},
            label: `${currentValue.parentHeader ? currentValue.parentHeader + ' ' : ''}${
                currentValue.header
            }: ${t('components.endlessList.descending')}`,
        };
        const asc = {
            value: {id: currentValue.id, desc: false},
            label: `${currentValue.parentHeader ? currentValue.parentHeader + ' ' : ''}${
                currentValue.header
            }: ${t('components.endlessList.ascending')}`,
        };
        return accumulator.concat([desc, asc]);
    }, [] as SelectOption<SortValue>[]);

interface Props {
    columns: FlatGridFieldConfig[];
    sortings?: InnerSortings;
    onChange: (value: InnerSortingsElement[]) => void;
    className?: string;
}

export const Sorting = ({columns, sortings, onChange, className}: Props) => {
    const {t} = useTranslation();
    const selectOptions = useMemo(
        () =>
            mapSelectOptions(
                columns.filter((elem) => elem.isSortable),
                t,
            ),
        [columns],
    );

    const handleChange = (option: SelectOption<SortValue> | null) => {
        if (option == null) {
            onChange([]);
            return;
        }

        onChange([
            {
                id: option.value!.id,
                value: option.value!.desc,
            },
        ]);
    };

    const getSelectedValues = () => {
        if (sortings == null) {
            return [];
        }

        const selectedKeys = Object.entries(sortings)
            .filter(([, value]) => value != null)
            .map(([key, value]) => `${key}_${value}`);

        return selectOptions.find(({value}) =>
            selectedKeys.includes(`${value!.id}_${value!.desc}`),
        );
    };

    return (
        <Select
            isClearable
            options={selectOptions}
            value={getSelectedValues()}
            onChange={handleChange}
            className={className}
        />
    );
};
